import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Search } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { obtenerMensajesChat, enviarMensaje, joinChat, subscribeToMessages, obtenerTodosLosUsuarios, subscribeToNewMessages, marcarMensajesComoLeidos, getUserConnectionStatus, subscribeToUserStatus, socket } from '../services/chatService';
import { motion, AnimatePresence } from 'framer-motion';

const Chat = ({ user, onClose }) => {
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [nuevoMensaje, setNuevoMensaje] = useState('');
    const [emisoresUnicos, setEmisoresUnicos] = useState([]);
    const [mensajesNoLeidos, setMensajesNoLeidos] = useState({});
    const [usuariosConectados, setUsuariosConectados] = useState(new Set());
    const mensajesContainerRef = useRef(null);
    const chatRef = useRef(null);
    const [busqueda, setBusqueda] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
    const [mostrarBusqueda, setMostrarBusqueda] = useState(false);
    const [usuariosEstados, setUsuariosEstados] = useState({});

    useEffect(() => {
        const cargarUsuarios = async () => {
            try {
                const response = await obtenerTodosLosUsuarios();
                if (response.success) {
                    const usuariosFiltrados = response.data.filter(u => u.id_usuario !== user.id);
                    setUsuarios(usuariosFiltrados);
                    
                    // Inicializar estados de usuarios
                    const estadosIniciales = {};
                    usuariosFiltrados.forEach(usuario => {
                        estadosIniciales[usuario.id_usuario] = usuario.activo ? 'online' : 'offline';
                    });
                    setUsuariosEstados(estadosIniciales);
                }
            } catch (error) {
                console.error('Error al cargar usuarios:', error);
            }
        };

        cargarUsuarios();
    }, [user]);

    useEffect(() => {
        if (user && user.id) {
            cargarMensajes();
            socket.emit('user_connected', { userId: user.id });

            // Suscribirse a cambios de estado de usuarios
            const unsubscribeStatus = subscribeToUserStatus((data) => {
                console.log('Estado de usuario actualizado:', data); // Para debug
                setUsuariosEstados(prev => ({
                    ...prev,
                    [data.userId]: data.status
                }));
            });

            // Obtener estados iniciales de todos los usuarios
            socket.on('users_connected', (usuarios) => {
                console.log('Usuarios conectados:', usuarios); // Para debug
                const estadosActualizados = {};
                usuarios.forEach(usuario => {
                    estadosActualizados[usuario.userId] = usuario.status;
                });
                setUsuariosEstados(prev => ({
                    ...prev,
                    ...estadosActualizados
                }));
            });

            return () => {
                socket.emit('user_disconnected', { userId: user.id });
                socket.off('users_connected');
                unsubscribeStatus();
            };
        }
    }, [user]);

    useEffect(() => {
        if (usuarioSeleccionado) {
            const chatId = [user.id, usuarioSeleccionado.id_usuario].sort().join('_');
            joinChat(user.id, usuarioSeleccionado.id_usuario);
            cargarMensajesConversacion(usuarioSeleccionado.id_usuario);
        }
    }, [usuarioSeleccionado]);

    useEffect(() => {
        if (user && user.id) {
            const unsubscribe = subscribeToMessages((message) => {
                if ((message.id_emisor === user.id && message.id_receptor === usuarioSeleccionado?.id_usuario) ||
                    (message.id_emisor === usuarioSeleccionado?.id_usuario && message.id_receptor === user.id)) {
                    const messageWithType = {
                        ...message,
                        tipo: message.id_emisor === user.id ? 'enviado' : 'recibido'
                    };
                    setMensajes(prev => [...prev, messageWithType]);
                    
                    if (message.id_emisor === usuarioSeleccionado?.id_usuario && message.id_receptor === user.id) {
                        socket.emit('messages_read', {
                            userId: user.id,
                            emisorId: usuarioSeleccionado.id_usuario
                        });
                    }
                    
                    scrollToBottom();
                }
            });

            return () => unsubscribe();
        }
    }, [user, usuarioSeleccionado]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chatRef.current && 
                !chatRef.current.contains(event.target) && 
                event.target.closest('[data-chat-toggle]')) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        scrollToBottom();
    }, [mensajes]);

    useEffect(() => {
        const filtrarUsuarios = () => {
            if (busqueda.trim() === '') {
                setUsuariosFiltrados([]);
            } else {
                setUsuariosFiltrados(
                    usuarios.filter((usuario) =>
                        `${usuario.nombre} ${usuario.apellidos || ''}`.toLowerCase().includes(busqueda.toLowerCase())
                    )
                );
            }
        };

        filtrarUsuarios();
    }, [busqueda, usuarios]);

    useEffect(() => {
        if (user && user.id) {
            const unsubscribeNewMessages = subscribeToNewMessages(user.id, (data) => {
                if (data.type === 'new_message') {
                    setMensajesNoLeidos(prev => ({
                        ...prev,
                        [data.id_emisor]: (prev[data.id_emisor] || 0) + 1
                    }));
                    
                    if (usuarioSeleccionado && data.id_emisor === usuarioSeleccionado.id_usuario) {
                        setMensajes(prev => [...prev, { ...data, tipo: 'recibido' }]);
                        marcarMensajesComoLeidos(user.id, usuarioSeleccionado.id_usuario);
                        scrollToBottom();
                    }
                }
            });

            const cargarMensajesNoLeidosPorEmisor = async () => {
                try {
                    const response = await obtenerMensajesChat(user.id);
                    if (response.success && response.data) {
                        const contadores = {};
                        response.data.forEach(mensaje => {
                            if (mensaje.estado === 'Enviado') {
                                contadores[mensaje.id_emisor] = (contadores[mensaje.id_emisor] || 0) + 1;
                            }
                        });
                        setMensajesNoLeidos(contadores);
                    }
                } catch (error) {
                    console.error('Error al cargar mensajes no leídos:', error);
                }
            };

            cargarMensajesNoLeidosPorEmisor();
            return () => unsubscribeNewMessages();
        }
    }, [user]);

    useEffect(() => {
        if (usuarioSeleccionado && user) {
            const marcarComoLeidos = async () => {
                try {
                    const response = await marcarMensajesComoLeidos(user.id, usuarioSeleccionado.id_usuario);
                    if (response.success) {
                        socket.emit('messages_read', {
                            userId: user.id,
                            emisorId: usuarioSeleccionado.id_usuario,
                            unreadCount: response.unreadCount
                        });
                        
                        setMensajesNoLeidos(prev => ({
                            ...prev,
                            [usuarioSeleccionado.id_usuario]: 0
                        }));
                    }
                } catch (error) {
                    console.error('Error al marcar mensajes como leídos:', error);
                }
            };

            marcarComoLeidos();
        }
    }, [usuarioSeleccionado, user]);

    const cargarMensajesConversacion = async (usuarioSeleccionadoId) => {
        try {
            setMensajes([]);

            const responseRecibidos = await obtenerMensajesChat(user.id);
            let mensajesRecibidos = [];

            if (responseRecibidos?.success && responseRecibidos?.data) {
                mensajesRecibidos = responseRecibidos.data.filter(mensaje => 
                    mensaje && 
                    mensaje.contenido && 
                    mensaje.id_emisor && 
                    mensaje.id_receptor && 
                    mensaje.id_emisor === usuarioSeleccionadoId &&
                    mensaje.id_receptor === user.id
                ).map(mensaje => ({
                    ...mensaje,
                    tipo: 'recibido'
                }));
            }

            const responseEnviados = await obtenerMensajesChat(usuarioSeleccionadoId);
            let mensajesEnviados = [];

            if (responseEnviados?.success && responseEnviados?.data) {
                mensajesEnviados = responseEnviados.data.filter(mensaje => 
                    mensaje && 
                    mensaje.contenido && 
                    mensaje.id_emisor && 
                    mensaje.id_receptor &&
                    mensaje.id_emisor === user.id &&
                    mensaje.id_receptor === usuarioSeleccionadoId
                ).map(mensaje => ({
                    ...mensaje,
                    tipo: 'enviado'
                }));
            }

            const mensajesConversacion = [...mensajesRecibidos, ...mensajesEnviados].sort((a, b) => 
                new Date(a.fecha_envio) - new Date(b.fecha_envio)
            );

            setMensajes(mensajesConversacion);
            scrollToBottom();
        } catch (error) {
            console.error('Error al cargar mensajes de la conversación:', error);
        }
    };

    const cargarMensajes = async () => {
        try {
            const response = await obtenerMensajesChat(user.id);

            if (response?.success && response?.data) {
                const usuariosMap = new Map();
                response.data.forEach(mensaje => {
                    if (mensaje.emisor && mensaje.id_emisor !== user.id) {
                        usuariosMap.set(mensaje.id_emisor, {
                            id_usuario: mensaje.id_emisor,
                            nombre: mensaje.emisor.nombre,
                            rol: mensaje.emisor.rol
                        });
                    }
                });

                const emisores = Array.from(usuariosMap.values());
                setEmisoresUnicos(emisores);
            }
        } catch (error) {
            // Manejar el error silenciosamente
        }
    };

    const formatearFecha = (fecha) => {
        try {
            if (!fecha) return '';
            
            const fechaObj = new Date(fecha);
            if (isNaN(fechaObj.getTime())) return '';
            
            return format(fechaObj, 'HH:mm', { locale: es });
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return '';
        }
    };

    const formatearFechaEncabezado = (fecha) => {
        try {
            if (!fecha) return '';
            
            const fechaObj = new Date(fecha);
            if (isNaN(fechaObj.getTime())) return '';
            
            const hoy = new Date();
            const ayer = new Date(hoy);
            ayer.setDate(ayer.getDate() - 1);
            
            if (fechaObj.toDateString() === hoy.toDateString()) {
                return 'Hoy';
            } else if (fechaObj.toDateString() === ayer.toDateString()) {
                return 'Ayer';
            } else {
                return format(fechaObj, "d 'de' MMMM 'de' yyyy", { locale: es });
            }
        } catch (error) {
            console.error('Error al formatear fecha de encabezado:', error);
            return '';
        }
    };

    const agruparMensajesPorFecha = (mensajes) => {
        const grupos = {};
        
        mensajes.forEach(mensaje => {
            if (mensaje && mensaje.fecha_envio) {
                const fecha = new Date(mensaje.fecha_envio);
                const fechaKey = fecha.toDateString();
                
                if (!grupos[fechaKey]) {
                    grupos[fechaKey] = [];
                }
                grupos[fechaKey].push(mensaje);
            }
        });
        
        return Object.entries(grupos).map(([fecha, mensajes]) => ({
            fecha: new Date(fecha),
            mensajes
        }));
    };

    const handleEnviarMensaje = async (e) => {
        e.preventDefault();
        if (!nuevoMensaje.trim() || !usuarioSeleccionado?.id_usuario || !user?.id) return;

        try {
            const ahora = new Date();

            const mensajeEnviado = await enviarMensaje(user.id, {
                id_receptor: usuarioSeleccionado.id_usuario,
                contenido: nuevoMensaje.trim(),
                estado: 'Enviado',
                fecha_envio: ahora.toISOString(),
                hora: ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            });
            
            if (mensajeEnviado) {
                const nuevoMensajeConTipo = {
                    ...mensajeEnviado,
                    tipo: 'enviado'
                };
                setMensajes(prev => [...prev, nuevoMensajeConTipo]);
                scrollToBottom();
            }
            
            setNuevoMensaje('');
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
        }
    };

    const scrollToBottom = () => {
        if (mensajesContainerRef.current) {
            mensajesContainerRef.current.scrollTop = mensajesContainerRef.current.scrollHeight;
        }
    };

    const manejarBusqueda = (e) => {
        setBusqueda(e.target.value);
    };

    const seleccionarUsuarioBusqueda = (usuario) => {
        setUsuarioSeleccionado(usuario);
        setBusqueda('');
        setMostrarBusqueda(false);
    };

    // Función para renderizar el estado del usuario
    const renderEstadoUsuario = (userId) => {
        const estado = usuariosEstados[userId];
        return (
            <div className="relative flex items-center">
                <div 
                    className={`w-3 h-3 rounded-full ${
                        estado === 'online' 
                            ? 'bg-green-500 animate-pulse ring-4 ring-green-100' 
                            : 'bg-gray-400 ring-4 ring-gray-100'
                    } absolute -bottom-1 -right-1 border-[1.5px] border-white shadow-sm`}
                    title={estado === 'online' ? 'En línea' : 'Desconectado'}
                />
            </div>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, x: 100 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: 100 }}
            transition={{ duration: 0.2 }}
            ref={chatRef}
            className="fixed top-20 right-4 h-[calc(100vh-10rem)] w-[95vw] md:w-96 bg-white shadow-xl flex flex-col rounded-lg overflow-hidden"
        >
            <div className="p-3 md:p-4 border-b flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                <h3 className="font-semibold text-base md:text-lg">Chat</h3>
                <div className="flex items-center space-x-1.5 md:space-x-2">
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setMostrarBusqueda(!mostrarBusqueda)}
                        className="text-white hover:text-gray-200 transition-all p-1 rounded-full hover:bg-blue-700/50 backdrop-blur-sm"
                    >
                        <Search className="h-4 w-4 md:h-5 md:w-5" />
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onClose}
                        className="text-white hover:text-gray-200 transition-all p-1 rounded-full hover:bg-blue-700/50 backdrop-blur-sm"
                    >
                        <X className="h-4 w-4 md:h-5 md:w-5" />
                    </motion.button>
                </div>
            </div>

            <AnimatePresence>
                {mostrarBusqueda && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-3 md:p-4 border-b bg-white/95 backdrop-blur-sm overflow-hidden"
                    >
                        <motion.input
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            type="text"
                            placeholder="Buscar usuario por nombre..."
                            value={busqueda}
                            onChange={manejarBusqueda}
                            className="w-full px-3 py-2 text-sm md:text-base border rounded-lg focus:ring-2 bg-white focus:ring-blue-500 focus:border-transparent shadow-sm transition-all duration-200 hover:shadow-md"
                            autoFocus
                        />
                        <AnimatePresence>
                            {usuariosFiltrados.length > 0 && (
                                <motion.ul
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="mt-2 border rounded-lg max-h-36 md:max-h-48 overflow-y-auto bg-white shadow-lg"
                                >
                                    {usuariosFiltrados.map((usuario, index) => (
                                        <motion.li
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            key={usuario.id_usuario}
                                            className="p-2 md:p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-all duration-200"
                                            onClick={() => seleccionarUsuarioBusqueda(usuario)}
                                        >
                                            <div className="flex items-center space-x-2 md:space-x-3">
                                                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
                                                    <span className="text-blue-600 font-medium text-sm md:text-base">
                                                        {usuario.nombre.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="overflow-hidden">
                                                    <p className="font-medium text-sm md:text-base truncate">{usuario.nombre} {usuario.apellidos || ''}</p>
                                                    <p className="text-xs md:text-sm text-gray-500 truncate">{usuario.rol}</p>
                                                </div>
                                            </div>
                                        </motion.li>
                                    ))}
                                </motion.ul>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>

            {!usuarioSeleccionado ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex-1 overflow-hidden"
                >
                    <div className="h-full overflow-y-auto custom-scrollbar">
                        <AnimatePresence>
                            {emisoresUnicos.length > 0 ? (
                                emisoresUnicos.map((emisor, index) => (
                                    <motion.div
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ 
                                            delay: index * 0.03,
                                            duration: 0.2,
                                            ease: "easeOut"
                                        }}
                                        key={emisor.id_usuario}
                                        onClick={() => setUsuarioSeleccionado(emisor)}
                                        className="py-2 md:py-3 px-3 md:px-4 mx-2 my-1.5 hover:bg-blue-50/80 cursor-pointer rounded-xl flex items-center space-x-2.5 md:space-x-3.5 transition-all duration-200 hover:shadow-md group relative overflow-hidden"
                                        whileHover={{ 
                                            scale: 1.02,
                                            transition: { duration: 0.2 }
                                        }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <div className="relative">
                                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-md flex-shrink-0 group-hover:shadow-lg transition-shadow duration-200 border-2 border-white">
                                                <span className="text-blue-600 font-semibold text-base md:text-lg">
                                                    {emisor.nombre.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            {renderEstadoUsuario(emisor.id_usuario)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-semibold text-gray-900 text-sm md:text-[15px] truncate pr-2 tracking-tight">
                                                    {emisor.nombre}
                                                </h4>
                                                <div className="flex items-center space-x-1.5 md:space-x-2">
                                                    {mensajesNoLeidos[emisor.id_usuario] > 0 && (
                                                        <motion.span 
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 md:px-2.5 md:py-1 rounded-full shadow-sm flex items-center justify-center min-w-[20px]"
                                                        >
                                                            {mensajesNoLeidos[emisor.id_usuario]}
                                                        </motion.span>
                                                    )}
                                                    <span className="text-sm md:text-base text-gray-400 flex-shrink-0 opacity-75 group-hover:opacity-100 transition-opacity">
                                                        {emisor.rol === 'Padre' ? '👨‍👦' : 
                                                         emisor.rol === 'Profesor' ? '👨‍🏫' : 
                                                         emisor.rol === 'Administrador' ? '⚡' : '👤'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-1.5 md:space-x-2 mt-0.5">
                                                <p className="text-xs md:text-sm text-gray-500 truncate font-medium">
                                                    {emisor.rol.charAt(0) + emisor.rol.slice(1).toLowerCase()}
                                                </p>
                                                <span className="text-gray-300">•</span>
                                                <span className={`text-xs ${
                                                    usuariosEstados[emisor.id_usuario] === 'online' 
                                                        ? 'text-green-500 font-medium' 
                                                        : 'text-gray-400'
                                                }`}>
                                                    {usuariosEstados[emisor.id_usuario] === 'online' ? 'En línea' : 'Desconectado'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/0 to-blue-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                                    </motion.div>
                                ))
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="p-4 text-center text-gray-500 text-xs md:text-sm"
                                >
                                    No hay conversaciones recientes
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            ) : (
                <div className="flex-1 flex flex-col h-full overflow-hidden">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 md:p-4 border-b bg-white flex items-center space-x-3 md:space-x-4 shadow-sm"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setUsuarioSeleccionado(null)}
                            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
                        >
                            <X className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
                        </motion.button>

                        <div className="flex items-center space-x-2 md:space-x-3 flex-1">
                            <div className="relative">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-md border-2 border-white">
                                    <span className="text-blue-600 font-semibold text-sm md:text-base">
                                        {usuarioSeleccionado.nombre.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                {renderEstadoUsuario(usuarioSeleccionado.id_usuario)}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-1.5 md:space-x-2">
                                    <h4 className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                        {usuarioSeleccionado.nombre}
                                    </h4>
                                    <span className="text-lg md:text-xl opacity-90">
                                        {usuarioSeleccionado.rol === 'Padre' ? '👨‍👦' : 
                                         usuarioSeleccionado.rol === 'Profesor' ? '👨‍🏫' : 
                                         usuarioSeleccionado.rol === 'Administrador' ? '⚡' : '👤'}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1.5 md:space-x-2">
                                    <p className="text-xs md:text-sm text-gray-500 truncate">
                                        {usuarioSeleccionado.rol.charAt(0) + usuarioSeleccionado.rol.slice(1).toLowerCase()}
                                    </p>
                                    <span className="text-gray-300 text-xs md:text-sm">•</span>
                                    <span className={`text-xs md:text-sm ${
                                        usuariosEstados[usuarioSeleccionado.id_usuario] === 'online' 
                                            ? 'text-green-500 font-medium' 
                                            : 'text-gray-400'
                                    }`}>
                                        {usuariosEstados[usuarioSeleccionado.id_usuario] === 'online' ? 'En línea' : 'Desconectado'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <div
                        ref={mensajesContainerRef}
                        className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 bg-[#f0f2f5]"
                    >
                        <AnimatePresence>
                            {agruparMensajesPorFecha(mensajes.filter(mensaje => mensaje && mensaje.contenido)).map((grupo, grupoIndex) => (
                                <motion.div
                                    key={grupo.fecha.toISOString()}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-1.5 md:space-y-2"
                                >
                                    <div className="flex justify-center">
                                        <span className="bg-white px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-xs text-gray-500 shadow-sm">
                                            {formatearFechaEncabezado(grupo.fecha)}
                                        </span>
                                    </div>
                                    
                                    {grupo.mensajes.map((mensaje, index) => (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            transition={{ 
                                                delay: index * 0.03,
                                                duration: 0.2,
                                                ease: "easeOut"
                                            }}
                                            key={`${grupoIndex}-${index}`}
                                            className={`flex items-end space-x-1 ${mensaje.tipo === 'enviado' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            {mensaje.tipo !== 'enviado' && (
                                                <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0 mb-1 shadow-sm">
                                                    <span className="text-gray-600 text-[10px] md:text-xs font-medium">
                                                        {usuarioSeleccionado.nombre.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                className={`relative group max-w-[75%] md:max-w-[70%] ${mensaje.tipo === 'enviado' ? 'ml-2' : 'mr-2'}`}
                                            >
                                                <div
                                                    className={`px-2.5 md:px-3 py-[5px] md:py-[6px] break-words relative
                                                        ${mensaje.tipo === 'enviado' 
                                                            ? 'bg-[#0084ff] text-white rounded-[12px] md:rounded-[15px] before:content-[""] before:absolute before:right-[-6px] md:before:right-[-8px] before:bottom-[6px] before:border-[6px] md:before:border-[8px] before:border-transparent before:border-l-[#0084ff]' 
                                                            : 'bg-white text-gray-800 rounded-[12px] md:rounded-[15px] before:content-[""] before:absolute before:left-[-6px] md:before:left-[-8px] before:bottom-[6px] before:border-[6px] md:before:border-[8px] before:border-transparent before:border-r-white'
                                                        }
                                                        group-hover:shadow-sm transition-all duration-300 ease-in-out
                                                        ${mensaje.tipo === 'enviado' ? 'group-hover:bg-[#007aff] before:group-hover:border-l-[#007aff]' : 'group-hover:bg-[#f8f8f8] before:group-hover:border-r-[#f8f8f8]'}`}
                                                >
                                                    <p className="whitespace-pre-wrap text-[13px] md:text-[15px] leading-[1.3] md:leading-[1.4] font-[-apple-system,system-ui] tracking-[-0.2px]">
                                                        {mensaje.contenido}
                                                    </p>
                                                    <div className={`flex items-center justify-end mt-[2px] space-x-1
                                                        ${mensaje.tipo === 'enviado' ? 'text-white/80' : 'text-gray-400'}`}
                                                    >
                                                        <span className="text-[9px] md:text-[10px] font-light">
                                                            {formatearFecha(mensaje.fecha_envio)}
                                                        </span>
                                                        {mensaje.tipo === 'enviado' && (
                                                            <span className="text-[9px] md:text-[10px] ml-[2px]">✓</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>

                                            {mensaje.tipo === 'enviado' && (
                                                <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0 mb-1 shadow-sm">
                                                    <span className="text-blue-600 text-[10px] md:text-xs font-medium">
                                                        {user.nombre ? user.nombre.charAt(0).toUpperCase() : 'U'}
                                                    </span>
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <motion.form
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onSubmit={handleEnviarMensaje}
                        className="p-3 md:p-4 border-t bg-white rounded-b-lg shadow-lg"
                    >
                        <div className="flex space-x-1.5 md:space-x-2">
                            <motion.input
                                whileFocus={{ scale: 1.01 }}
                                type="text"
                                value={nuevoMensaje}
                                onChange={(e) => setNuevoMensaje(e.target.value)}
                                placeholder="Escribe un mensaje..."
                                className="flex-1 px-3 md:px-4 py-1.5 md:py-2 bg-white border rounded-full text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition-all duration-200 hover:shadow-md"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleEnviarMensaje(e);
                                    }
                                }}
                            />
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                type="submit"
                                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2 md:p-3 rounded-full hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform"
                                disabled={!nuevoMensaje.trim()}
                            >
                                <Send className="h-4 w-4 md:h-5 md:w-5" />
                            </motion.button>
                        </div>
                    </motion.form>
                </div>
            )}

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                    @media (min-width: 768px) {
                        width: 6px;
                    }
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
            `}</style>
        </motion.div>
    );
};

export default Chat;